import Vue from 'vue'

class JobRepository {

  jobs(data = {}) {
    return new Promise((resolve, reject) => {
      Vue.auth.fetch({
        url: '/api/jobs',
        method: 'post',
        data,
      })
      .then(response => {
        resolve(response.data)
      }).catch(err => {
        reject(err)
      })
    })
  }

  metrics() {
    return new Promise((resolve, reject) => {
      Vue.auth.fetch({
        url: '/api/jobs/metrics',
        method: 'get',
      })
      .then(response => {
        resolve(response.data)
      }).catch(err => {
        reject(err)
      })
    })
  }

  options(data = {}) {
    return new Promise((resolve, reject) => {
      Vue.auth.fetch({
        url: '/api/jobs/options',
        method: 'post',
        data,
      })
        .then(response => resolve(response.data))
        .catch(err => reject(err))
    })
  }

  executionLogs(executionId, params = {}) {
    // JSON mode - supports direction, limit, beforeDate, beforeId, afterDate, afterId
    return new Promise((resolve, reject) => {
      Vue.auth.fetch({
        url: `/api/jobs/executions/${encodeURIComponent(executionId)}/logs`,
        method: 'get',
        data: params,
      })
        .then(response => resolve(response.data))
        .catch(err => reject(err))
    })
  }

    executionLogsStream(executionId, { afterDate = null, afterId = null, limit = 200, signal } = {}) {
        if (!executionId || executionId === 'null' || executionId === 'undefined') {
            return Promise.reject(Object.assign(new Error('Missing executionId'), { status: 400 }));
        }

        const params = new URLSearchParams();
        params.set('limit', String(limit));
        params.set('format', 'ndjson');          // <-- NDJSON
        if (afterDate) params.set('afterDate', afterDate);
        if (afterId)   params.set('afterId', afterId);

        const token = (Vue && Vue.auth && Vue.auth.token && Vue.auth.token()) ? Vue.auth.token().accessToken : null;
        const headers = new Headers();
        headers.set('Accept', 'application/x-ndjson');   // <-- NDJSON
        if (token) headers.set('Authorization', `Bearer ${token}`);

        return fetch(`/api/jobs/executions/${encodeURIComponent(executionId)}/logs?` + params.toString(), {
            method: 'GET',
            headers,
            signal,
        }).then(async (res) => {
            if (!res.ok) {
                const body = await res.text().catch(() => '');
                const err = new Error(`HTTP ${res.status}`);
                err.status = res.status;
                err.body = body;
                throw err;
            }
            const reader  = res.body?.getReader ? res.body.getReader() : null;
            const decoder = new TextDecoder('utf-8');
            return { reader, decoder, response: res };
        });
    }


    executionLogsJSON(executionId, { limit = 1 } = {}) {
        const params = new URLSearchParams();
        params.set('limit', String(limit));
        params.set('order', 'asc');
        const token = (Vue && Vue.auth && Vue.auth.token && Vue.auth.token()) ? Vue.auth.token().accessToken : null;
        const headers = {};
        if (token) headers['Authorization'] = `Bearer ${token}`;
        return Vue.auth.fetch({
            url: `/api/jobs/executions/${encodeURIComponent(executionId)}/logs`,
            method: 'get',
            data: { limit, order: 'asc' },
            headers,
        }).then(res => res.data);
    }

  // ============================================
  // Failed Jobs API
  // ============================================

  failedJobs(params = {}) {
    const data = { limit: 50, offset: 0, ...params }
    return Vue.auth.fetch({
      url: '/api/jobs/failed',
      method: 'get',
      data
    }).then(res => res.data)
  }

  // The failures still to handle, grouped by job and error: { groups, unhandled }
  failureGroups() {
    return Vue.auth.fetch({ url: '/api/jobs/failed/groups', method: 'get' }).then(res => res.data)
  }

  // Mark failures as handled: { jobIds } or { definitionName, errors }, with an optional note → { handled }
  handleFailures(payload) {
    return Vue.auth.fetch({ url: '/api/jobs/failed/handle', method: 'post', data: payload }).then(res => res.data)
  }

  getJob(jobId) {
    return Vue.auth.fetch({
      url: `/api/jobs/${encodeURIComponent(jobId)}`,
      method: 'get'
    }).then(res => res.data)
  }

  retryJob(jobId) {
    return Vue.auth.fetch({
      url: `/api/jobs/${encodeURIComponent(jobId)}/retry`,
      method: 'post'
    }).then(res => res.data)
  }

  deleteJob(jobId) {
    return Vue.auth.fetch({
      url: `/api/jobs/${encodeURIComponent(jobId)}`,
      method: 'delete'
    }).then(res => res.data)
  }

  cancelJob(jobId) {
    return Vue.auth.fetch({
      url: `/api/jobs/${encodeURIComponent(jobId)}/cancel`,
      method: 'post'
    }).then(res => res.data)
  }

  // ============================================
  // Schedules API
  // ============================================

  schedules(params = {}) {
    return Vue.auth.fetch({
      url: '/api/jobs/schedules',
      method: 'get',
      data: params
    }).then(res => res.data)
  }

  pauseSchedule(scheduleId) {
    return Vue.auth.fetch({
      url: `/api/jobs/schedules/${encodeURIComponent(scheduleId)}/pause`,
      method: 'post'
    }).then(res => res.data)
  }

  resumeSchedule(scheduleId) {
    return Vue.auth.fetch({
      url: `/api/jobs/schedules/${encodeURIComponent(scheduleId)}/resume`,
      method: 'post'
    }).then(res => res.data)
  }

  executeSchedules(limit = 100) {
    return Vue.auth.fetch({
      url: '/api/jobs/schedules/execute',
      method: 'post',
      data: { limit }
    }).then(res => res.data)
  }

  createSchedule(data) {
    return Vue.auth.fetch({
      url: '/api/jobs/schedules',
      method: 'post',
      data
    }).then(res => res.data)
  }

  updateSchedule(scheduleId, data) {
    return Vue.auth.fetch({
      url: `/api/jobs/schedules/${encodeURIComponent(scheduleId)}`,
      method: 'put',
      data
    }).then(res => res.data)
  }

  deleteSchedule(scheduleId) {
    return Vue.auth.fetch({
      url: `/api/jobs/schedules/${encodeURIComponent(scheduleId)}`,
      method: 'delete'
    }).then(res => res.data)
  }

  // ============================================
  // Available Jobs API (Discovery)
  // ============================================

  availableJobs() {
    return Vue.auth.fetch({
      url: '/api/jobs/available',
      method: 'get'
    }).then(res => res.data)
  }

  // ============================================
  // Transports API
  // ============================================

  transports() {
    return Vue.auth.fetch({
      url: '/api/jobs/transports',
      method: 'get'
    }).then(res => res.data)
  }

  // ============================================
  // Job Definitions API
  // ============================================

  jobDefinitions(params = {}) {
    return Vue.auth.fetch({
      url: '/api/jobs/definitions',
      method: 'get',
      data: params
    }).then(res => res.data)
  }

  getJobDefinition(definitionId) {
    return Vue.auth.fetch({
      url: `/api/jobs/definitions/${encodeURIComponent(definitionId)}`,
      method: 'get'
    }).then(res => res.data)
  }

  updateDefinitionStatus(definitionId, data) {
    return Vue.auth.fetch({
      url: `/api/jobs/definitions/${encodeURIComponent(definitionId)}/status`,
      method: 'put',
      data
    }).then(res => res.data)
  }

  deleteDefinition(definitionId) {
    return Vue.auth.fetch({
      url: `/api/jobs/definitions/${encodeURIComponent(definitionId)}`,
      method: 'delete'
    }).then(res => res.data)
  }

  syncDefinitionsPreview() {
    return Vue.auth.fetch({
      url: '/api/jobs/definitions/sync',
      method: 'get'
    }).then(res => res.data)
  }

  syncDefinitions() {
    return Vue.auth.fetch({
      url: '/api/jobs/definitions/sync',
      method: 'post'
    }).then(res => res.data)
  }

  // ============================================
  // Circuit Breaker API
  // ============================================

  circuitBreakers() {
    return Vue.auth.fetch({
      url: '/api/jobs/circuit-breakers',
      method: 'get'
    }).then(res => res.data)
  }

  resetCircuitBreaker(serviceName) {
    return Vue.auth.fetch({
      url: `/api/jobs/circuit-breakers/${encodeURIComponent(serviceName)}/reset`,
      method: 'post'
    }).then(res => res.data)
  }

}

export default new JobRepository()